// usePrefetch — hover/touch prefetch hook for profile links.
// When the user hovers a username or video card for ~250 ms the profile
// fetch is fired in the background and stored in the SWR cache. By the
// time they click, the profile page paints instantly from cache.
//
// Persistent-shell merge (Iter-57): paired with the in-memory cache in
// /lib/cache.js so hover→click on a profile link is effectively free.
// No-op if the username already has a fresh cache entry.

import api from "./apiClient";
import { cacheGet, cacheSet } from "./cache";

const HOVER_DELAY_MS = 250;
const _inflight = new Set();

export const prefetchProfile = async (username) => {
  if (!username) return;
  const key = `profile:${username.toLowerCase()}`;
  if (cacheGet(key) || _inflight.has(key)) return;
  _inflight.add(key);
  try {
    const [profileRes, highlightedRes] = await Promise.allSettled([
      api.get(`/users/${username}`),
      api.get(`/users/${username}/videos/highlighted`),
    ]);
    if (profileRes.status === "fulfilled") {
      const highlighted = highlightedRes.status === "fulfilled"
        ? highlightedRes.value.data
        : [];
      cacheSet(key, { profile: profileRes.value.data, highlighted });
    }
  } catch (_) {
    // best-effort — silent on failure, the eventual visit will retry
  }
  _inflight.delete(key);
};

/**
 * Returns event handlers to spread on any element that links to a profile.
 * `data-prefetch-username` is the canonical attribute; React event handlers
 * are derived from it so any anchor anywhere in the app gets the same
 * treatment without per-page wiring.
 *
 * Usage:
 *   <Link to={`/${username}`} {...profilePrefetchProps(username)}>...</Link>
 */
export const profilePrefetchProps = (username) => {
  let timer = null;
  const start = () => {
    if (timer) return;
    timer = setTimeout(() => {
      prefetchProfile(username);
      timer = null;
    }, HOVER_DELAY_MS);
  };
  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return {
    onMouseEnter: start,
    onMouseLeave: cancel,
    onTouchStart: () => prefetchProfile(username),
    "data-prefetch-username": username,
  };
};
